import { useCallback } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseSideBandsEditDialogHandlerProps {
  editingSideBandsGroup: any | null;
  verticalLines: VerticalLine[];
  removeVerticalLine: (lineId: string) => void;
  addVerticalLine: (
    timestamp: any,
    values: any,
    xValue: number,
    label: string,
    color: string,
    isHarmonic: boolean,
    isFirstHarmonic: boolean,
    chartId: string,
    sectionKey: string,
    markerType: string | undefined,
    originalPrefix: string | undefined,
    groupId: string
  ) => void;
}

export interface UseSideBandsEditDialogHandlerReturn {
  handleSave: (
    labelPrefix: string,
    centerPosition: number,
    sidebandPosition: number,
    count: number
  ) => void;
}

/**
 * Handles SideBandsGroupEditDialog save action
 * Creates or updates side bands groups (C, D, E) with configurable center position, interval, and count
 */
export function useSideBandsEditDialogHandler({
  editingSideBandsGroup,
  verticalLines,
  removeVerticalLine,
  addVerticalLine,
}: UseSideBandsEditDialogHandlerProps): UseSideBandsEditDialogHandlerReturn {

  const handleSave = useCallback(
    (
      labelPrefix: string,
      centerPosition: number,
      sidebandPosition: number,
      count: number
    ) => {
      if (editingSideBandsGroup) {
        // Calculate interval from center and sideband positions
        const interval = Math.abs(sidebandPosition - centerPosition);

        // Find all lines in the old group (only if editing existing group)
        // Must match: same label prefix (C, D, or E), same color, same chartId
        const oldGroupLines = verticalLines.filter((line: VerticalLine) => {
          if (!line.isHarmonic || line.chartId !== editingSideBandsGroup.chartId) {
            return false;
          }
          // Type guard: ensure label is a string
          if (typeof line.label !== 'string') return false;
          // Extract label prefix from line label (e.g., "C" from "C0", "C1", "-C1")
          const labelMatch = line.label.match(/^-?([A-Z])/);
          if (!labelMatch) return false;
          const linePrefix = labelMatch[1];
          // Only include lines with the same prefix as the group being edited
          return linePrefix === editingSideBandsGroup.labelPrefix;
        });

        // Determine original prefix to preserve
        let originalPrefix: string | undefined = undefined;

        if (!editingSideBandsGroup.isNewGroup && oldGroupLines.length > 0) {
          // If editing existing group, check if it has an originalPrefix or extract from label
          const firstLine = oldGroupLines[0];
          if (firstLine.originalPrefix) {
            // Already has originalPrefix (was renamed before)
            originalPrefix = firstLine.originalPrefix;
          } else {
            // Extract current prefix - this is the original default
            const match = firstLine.label?.match(/^-?([A-Z])\d+/);
            if (match) {
              const currentPrefix = match[1];
              // Only set originalPrefix if the new labelPrefix is different (= custom name)
              if (currentPrefix !== labelPrefix) {
                originalPrefix = currentPrefix;
              }
            }
          }
        }

        // Remove old group lines (only if editing, not creating new)
        if (!editingSideBandsGroup.isNewGroup) {
          oldGroupLines.forEach((line: VerticalLine) => {
            removeVerticalLine(line.id);
          });
        }

        // Generate new carrier lines based on the updated parameters
        const sectionKey = editingSideBandsGroup.sectionKey;
        const color = editingSideBandsGroup.color;

        // Generate unique group ID for this side bands group (all carriers in this group share this ID)
        const groupId = editingSideBandsGroup.isNewGroup
          ? `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          : oldGroupLines.length > 0 && oldGroupLines[0].groupId
          ? oldGroupLines[0].groupId
          : `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create C0 (center)
        addVerticalLine(
          centerPosition, // timestamp
          {}, // values (empty object for carriers)
          centerPosition, // xValue
          `${labelPrefix}0`, // customLabel
          color, // customColor
          true, // isHarmonic
          true, // isFirstHarmonic
          editingSideBandsGroup.chartId, // chartId
          sectionKey, // sectionKey
          undefined, // markerType
          originalPrefix, // originalPrefix to track if renamed
          groupId // groupId - unique ID for this carrier group
        );

        // Create positive carriers (C1, C2, C3, etc.)
        for (let i = 1; i <= count; i++) {
          const position = centerPosition + interval * i;
          addVerticalLine(
            position, // timestamp
            {}, // values (empty object for carriers)
            position, // xValue
            `${labelPrefix}${i}`, // customLabel
            color, // customColor
            true, // isHarmonic
            false, // isFirstHarmonic
            editingSideBandsGroup.chartId, // chartId
            sectionKey, // sectionKey
            undefined, // markerType
            originalPrefix, // originalPrefix to track if renamed
            groupId // groupId - unique ID for this carrier group
          );
        }

        // Create negative carriers (-C1, -C2, -C3, etc.)
        for (let i = 1; i <= count; i++) {
          const position = centerPosition - interval * i;
          addVerticalLine(
            position, // timestamp
            {}, // values (empty object for carriers)
            position, // xValue
            `-${labelPrefix}${i}`, // customLabel
            color, // customColor
            true, // isHarmonic
            false, // isFirstHarmonic
            editingSideBandsGroup.chartId, // chartId
            sectionKey, // sectionKey
            undefined, // markerType
            originalPrefix, // originalPrefix to track if renamed
            groupId // groupId - unique ID for this carrier group
          );
        }
      }
    },
    [editingSideBandsGroup, verticalLines, removeVerticalLine, addVerticalLine]
  );

  return {
    handleSave,
  };
}
